import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import './UserAuth.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

const UserLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleAuthMode, setGoogleAuthMode] = useState('emergent'); // 'emergent' or 'self_hosted'
  const [googleClientId, setGoogleClientId] = useState(null);
  const navigate = useNavigate();

  // Get redirect parameter from URL
  const getRedirectPath = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('redirect') || '/setup';
  }, []);

  // Handle Emergent Auth callback
  const handleEmergentAuth = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      // Get session data from Emergent
      const response = await axios.get('https://demobackend.emergentagent.com/auth/v1/env/oauth/session-data', {
        headers: {
          'X-Session-ID': sessionId
        }
      });

      const { id, email: userEmail, name, picture, session_token } = response.data;

      // Send to our backend to create/update user
      const userResponse = await axios.post(`${API}/auth/google-login`, {
        google_id: id,
        email: userEmail,
        name: name,
        picture: picture,
        session_token: session_token
      });

      // Store session
      localStorage.setItem('user_session_token', userResponse.data.session_token);
      localStorage.setItem('user', JSON.stringify(userResponse.data.user));

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      toast.success(`Willkommen, ${name}!`);
      navigate(getRedirectPath());

    } catch (error) {
      console.error('Google auth error:', error);
      toast.error('Google Login fehlgeschlagen');
      setLoading(false);
    }
  }, [navigate, getRedirectPath]);

  // Handle Self-Hosted OAuth callback
  const handleSelfHostedCallback = useCallback(async (code) => {
    setLoading(true);
    try {
      const redirectUri = `${window.location.origin}/login`;
      
      const response = await axios.post(`${API}/auth/google/callback`, {
        code: code,
        redirect_uri: redirectUri
      });

      // Store session
      localStorage.setItem('user_session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);

      toast.success(`Willkommen, ${response.data.user.name || response.data.user.email}!`);
      navigate(getRedirectPath());

    } catch (error) {
      console.error('Google OAuth callback error:', error);
      toast.error(error.response?.data?.detail || 'Google Login fehlgeschlagen');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      setLoading(false);
    }
  }, [navigate, getRedirectPath]);

  // Check Google OAuth configuration on mount
  useEffect(() => {
    const checkGoogleConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/google/config`);
        setGoogleAuthMode(response.data.mode);
        if (response.data.client_id) {
          setGoogleClientId(response.data.client_id);
        }
      } catch (error) {
        console.log('Using default Emergent auth');
      }
    };
    checkGoogleConfig();
  }, []);

  // Check for OAuth callback parameters
  useEffect(() => {
    // Check for Emergent Auth session_id in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleEmergentAuth(sessionId);
      return;
    }

    // Check for Self-Hosted Google OAuth code in URL params
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      handleSelfHostedCallback(code);
    }
  }, [handleEmergentAuth, handleSelfHostedCallback]);

  const handleGoogleLogin = () => {
    if (googleAuthMode === 'self_hosted' && googleClientId) {
      // Self-hosted Google OAuth
      const redirectUri = `${window.location.origin}/login`;
      const scope = 'email profile';
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${googleClientId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=code` +
        `&scope=${encodeURIComponent(scope)}` +
        `&access_type=offline` +
        `&prompt=consent`;
      
      window.location.href = googleAuthUrl;
    } else {
      // Emergent Auth
      const redirectUrl = `${window.location.origin}/login`;
      window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      toast.error('Bitte Email und Passwort eingeben');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: email.trim(), // Backend expects 'username' field
        password: password.trim()
      });

      const { user, session_token } = response.data;

      // Check if regular user (not admin-only login)
      localStorage.setItem('user_session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));

      toast.success(`Willkommen zurück, ${user.name || user.username}!`);
      navigate(getRedirectPath());

    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login fehlgeschlagen');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-loading">
          <div className="loading-spinner"></div>
          <p>Anmeldung läuft...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-content">
        <Button 
          variant="ghost" 
          className="back-button"
          onClick={() => navigate('/choose-mode')}
        >
          <ArrowLeft size={20} /> Zurück
        </Button>

        <Card className="auth-card">
          <CardHeader>
            <CardTitle>Willkommen zurück!</CardTitle>
            <CardDescription>Melde dich an, um fortzufahren</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Login */}
            <Button
              onClick={handleGoogleLogin}
              className="google-button"
              variant="outline"
              disabled={loading}
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="google-icon" />
              Mit Google anmelden
            </Button>

            <div className="divider">
              <span>oder</span>
            </div>

            {/* Email/Password Login */}
            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <Label htmlFor="email">Email oder Username</Label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.com oder username"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Anmeldung...' : 'Anmelden'}
              </Button>
            </form>

            <div className="auth-footer">
              <p>
                Noch kein Account? 
                <a href="/register"> Jetzt registrieren</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserLogin;
